/** @file Konva Stage + 4 layers + ResizeObserver. */
import Konva from "konva";
import { createTransform } from "../core/coords.js";
import { appStore } from "../core/state.js";
import { drawField } from "./field.js";
import { markDirty } from "./renderer.js";
import { attachPointerEvents } from "../input/pointer.js";
import { mountTokens, unmountTokens } from "./tokens.js";
import { mountDrawings, unmountDrawings } from "./drawings.js";

Konva.pixelRatio = window.devicePixelRatio || 1;

/** @type {Konva.Stage | null} */
let stage = null;
/** @type {Konva.Layer | null} */
let fieldLayer = null;
/** @type {Konva.Layer | null} */
let playersLayer = null;
/** @type {Konva.Layer | null} */
let drawingsLayer = null;
/** @type {Konva.Layer | null} */
let overlayLayer = null;
/** @type {ResizeObserver | null} */
let resizeObserver = null;
let rafPending = false;

/**
 * @param {HTMLElement} container
 * @param {{ onTokenTap: (info: { tokenId: string }) => void }} handlers
 */
export function mount(container, handlers) {
  if (stage) {
    return;
  }

  const rect = container.getBoundingClientRect();
  const t = createTransform({
    containerWidthPx: rect.width,
    containerHeightPx: rect.height,
    dpr: window.devicePixelRatio || 1,
  });
  appStore.set({ transform: t });

  stage = new Konva.Stage({
    container: /** @type {HTMLDivElement} */ (container),
    width: rect.width,
    height: rect.height,
  });

  fieldLayer = new Konva.Layer({ listening: false });
  playersLayer = new Konva.Layer();
  drawingsLayer = new Konva.Layer();
  overlayLayer = new Konva.Layer({ listening: false });

  stage.add(fieldLayer);
  stage.add(drawingsLayer);
  stage.add(playersLayer);
  stage.add(overlayLayer);

  drawField(fieldLayer, t);
  mountTokens(playersLayer, handlers.onTokenTap);
  mountDrawings(drawingsLayer, overlayLayer);
  attachPointerEvents(stage);

  resizeObserver = new ResizeObserver(() => {
    if (rafPending) {
      return;
    }
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      handleResize(container);
    });
  });
  resizeObserver.observe(container);

  window.addEventListener("orientationchange", () => handleResize(container));
}

/**
 * @param {HTMLElement} container
 */
function handleResize(container) {
  if (!stage || !fieldLayer) {
    return;
  }
  const rect = container.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }
  const t = createTransform({
    containerWidthPx: rect.width,
    containerHeightPx: rect.height,
    dpr: window.devicePixelRatio || 1,
  });
  appStore.set({ transform: t });

  stage.size({ width: rect.width, height: rect.height });
  drawField(fieldLayer, t);
  markDirty(fieldLayer);
}

/**
 * @returns {Konva.Stage | null}
 */
export function getStage() {
  return stage;
}

/**
 * @returns {{ field: Konva.Layer, players: Konva.Layer, drawings: Konva.Layer, overlay: Konva.Layer } | null}
 */
export function getLayers() {
  if (!fieldLayer || !playersLayer || !drawingsLayer || !overlayLayer) {
    return null;
  }
  return {
    field: fieldLayer,
    players: playersLayer,
    drawings: drawingsLayer,
    overlay: overlayLayer,
  };
}

export function destroy() {
  unmountTokens();
  unmountDrawings();
  resizeObserver?.disconnect();
  resizeObserver = null;
  stage?.destroy();
  stage = null;
  fieldLayer = null;
  playersLayer = null;
  drawingsLayer = null;
  overlayLayer = null;
}
